import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { axiomTheme } from '../../styles/designTokens';
import { TRADE_ENTRY_MENU } from '../../utils/ticketTaxonomy';
import {
  CollapsedRail,
  CollapsedRailItem,
  CollapsedRailSection,
  WorkspaceRailToggle,
} from '../shared/Workspace';

// Import all ticket screens
import FixedIncomeTicketScreen from '../tickets/FixedIncomeTicketScreen';
import EquityTicketScreen from '../tickets/EquityTicketScreen';
import PrivateFixedIncomeTicketScreen from '../tickets/PrivateFixedIncomeTicketScreen';
import RealEstateTicketScreen from '../tickets/RealEstateTicketScreen';
import RealEstateEquityTicketScreen from '../tickets/RealEstateEquityTicketScreen';
import RMLTicketScreen from '../tickets/RMLTicketScreen';
import PrivateEquityTicketScreen from '../tickets/PrivateEquityTicketScreen';
import PortcoDebtTicketScreen from '../tickets/PortcoDebtTicketScreen';
import CLOEquityTicketScreen from '../tickets/CLOEquityTicketScreen';
import ABFFacilityTicketScreen from '../tickets/ABFFacilityTicketScreen';
import RMBSTrancheTicketScreen from '../tickets/RMBSTrancheTicketScreen';
import ABSTrancheTicketScreen from '../tickets/ABSTrancheTicketScreen';
import MSRTicketScreen from '../tickets/MSRTicketScreen';
import CMBSTrancheTicketScreen from '../tickets/CMBSTrancheTicketScreen';
import DerivativesIRSwapTicketScreen from '../tickets/DerivativesIRSwapTicketScreen';
import DerivativesTRSwapTicketScreen from '../tickets/DerivativesTRSwapTicketScreen';
import DerivativesCurrencySwapTicketScreen from '../tickets/DerivativesCurrencySwapTicketScreen';
import DerivativesCDSwapTicketScreen from '../tickets/DerivativesCDSwapTicketScreen';
import DerivativesFuturesTicketScreen from '../tickets/DerivativesFuturesTicketScreen';
import DerivativesOptionsTicketScreen from '../tickets/DerivativesOptionsTicketScreen';
import DerivativesSwaptionTicketScreen from '../tickets/DerivativesSwaptionTicketScreen';
import FXTicketScreen from '../tickets/FXTicketScreen';
import RepoTicketScreen from '../tickets/RepoTicketScreen';
import CashTransferTicketScreen from '../tickets/CashTransferTicketScreen';
import CreditPostedEventDetailScreen from '../tickets/CreditPostedEventDetailScreen';
import MultiLegTradeTicket from '../MultiLegTradeTicket';

/** Bare `/trade-entry/derivatives` is not a valid leaf route; preserve router state when redirecting. */
function DerivativesLegacyRedirect() {
  const { state } = useLocation();
  return <Navigate to="/trade-entry/derivatives/ir-swap" replace state={state} />;
}

/**
 * InstitutionalTradeEntry - Complete trade entry screen for institutional clients.
 *
 * Features:
 * - Full asset class support (Fixed Income, Equity, Private Credit, Real Estate, Derivatives, FX, Repo)
 * - Asset Manager and Portfolio Manager selectors
 * - Fund/Portfolio selector with block trade allocation support
 * - Benchmark and mandate awareness
 * - FIX connectivity integration
 *
 * This is a self-contained component that handles all trade entry routing internally.
 * It renders the appropriate ticket screen based on the current path.
 */

const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  display: flex;
  flex-direction: column;
  padding: ${axiomTheme.spacing.page};
  color: ${axiomTheme.color.text.secondary};
`;

const PageLayout = styled.div<{ $sidebarCollapsed?: boolean }>`
  display: grid;
  grid-template-columns: ${p => p.$sidebarCollapsed ? '64px minmax(0, 1fr)' : 'minmax(230px, 270px) minmax(0, 1fr)'};
  gap: 1rem;
  align-items: start;
  transition: grid-template-columns 0.18s ease;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.div<{ $collapsed?: boolean }>`
  position: sticky;
  top: 80px;
  border: 1px solid ${axiomTheme.color.border.subtle};
  border-radius: ${axiomTheme.radius.xxl};
  background: ${axiomTheme.surface.rail};
  padding: ${p => p.$collapsed ? 0 : '0.9rem'};
  display: flex;
  flex-direction: column;
  min-width: ${p => p.$collapsed ? '64px' : '0'};

  @media (max-width: 980px) {
    position: static;
  }
`;

const SidebarHeading = styled.div`
  font-size: 0.7rem;
  font-weight: 850;
  color: ${axiomTheme.color.text.faint};
  text-transform: uppercase;
  letter-spacing: 0.16em;
  padding: 0 0.35rem;
  margin-bottom: 1rem;
`;

const SidebarNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const SidebarTab = styled.button<{ active?: boolean }>`
  padding: 0.75rem 1rem;
  border-radius: ${axiomTheme.radius.lg};
  font-weight: 800;
  font-size: 0.82rem;
  cursor: pointer;
  transition: all 0.16s ease;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border: 1px solid ${props => props.active ? axiomTheme.color.border.focus : 'transparent'};
  background: ${props => props.active ? 'rgba(14, 116, 144, 0.24)' : 'transparent'};
  color: ${props => props.active ? axiomTheme.color.text.primary : axiomTheme.color.text.secondary};
  position: relative;
  text-align: left;

  ${props => props.active && `
    &::before {
      content: '';
      position: absolute;
      left: -0.9rem;
      top: 50%;
      transform: translateY(-50%);
      height: 60%;
      width: 4px;
      background: ${axiomTheme.color.tone.cyan};
      border-radius: 0 4px 4px 0;
    }
  `}

  &:hover {
    background: ${props => props.active ? 'rgba(14, 116, 144, 0.28)' : 'rgba(148, 163, 184, 0.08)'};
    color: ${axiomTheme.color.text.primary};
  }
`;

const ContentArea = styled.div`
  min-width: 0;
`;

// Full asset class list for institutional clients
type MenuItem = {
  path?: string;
  label: string;
  icon?: string;
  subItems?: { path: string; label: string; icon?: string }[];
};

type FlatMenuItem = {
  path: string;
  label: string;
  icon?: string;
};

const pathMatches = (currentPath: string, itemPath: string) =>
  currentPath === itemPath || currentPath.startsWith(`${itemPath}/`);

// The menu is DERIVED from the ticket taxonomy (utils/ticketTaxonomy.ts) —
// the single source of truth binding each ticket to its governed L1/L2
// classification placement. A dev-time assertion in that module throws when a
// group contradicts the taxonomy, so the pre-2026-08 drift (Leveraged Loan
// under "Private Credit" while the register showed it under Public Credit)
// cannot recur silently. Groups with a single ticket render as flat items.
const menuItems: MenuItem[] = TRADE_ENTRY_MENU.map(group =>
  group.items.length === 1
    ? { path: group.items[0].path, label: group.label, icon: group.icon }
    : {
        label: group.label,
        icon: group.icon,
        subItems: group.items.map(({ path, label, icon }) => ({ path, label, icon })),
      }
);

const InstitutionalTradeEntry: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Determine current asset class from path
  const currentPath = location.pathname.replace('/trade-entry/', '').replace('/trade-entry', '');
  
  // Auto-expand the group containing the active path. Derived from the menu
  // itself — hand-coded per-group booleans keyed by label drifted every time
  // a group was renamed or a ticket moved.
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const item of menuItems) {
      if (item.subItems) {
        initial[item.label] = item.subItems.some(sub => pathMatches(currentPath, sub.path));
      }
    }
    return initial;
  });

  useEffect(() => {
    const activeMenu = menuItems.find(item =>
      item.subItems?.some(sub => pathMatches(currentPath, sub.path))
    );
    if (!activeMenu) return;
    setExpandedMenus(prev => (
      prev[activeMenu.label] ? prev : { ...prev, [activeMenu.label]: true }
    ));
  }, [currentPath]);

  const toggleMenu = (label: string) => {
    setExpandedMenus(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  const handleAssetClassClick = (path: string) => {
    navigate(`/trade-entry/${path}`);
  };

  const collapsedSections = menuItems.map(item => {
    const items: FlatMenuItem[] = item.subItems
      ? item.subItems
      : item.path
        ? [{ path: item.path, label: item.label, icon: item.icon }]
        : [];
    return { label: item.label, items };
  }).filter(section => section.items.length > 0);

  return (
    <Container>
      <PageLayout $sidebarCollapsed={sidebarCollapsed}>
        <Sidebar $collapsed={sidebarCollapsed}>
          <WorkspaceRailToggle
            collapsed={sidebarCollapsed}
            label="Trade Entry"
            onToggle={() => setSidebarCollapsed(prev => !prev)}
          />
          {sidebarCollapsed ? (
            <CollapsedRail>
              {collapsedSections.map(section => (
                <CollapsedRailSection key={section.label}>
                  {section.items.map(item => (
                    <CollapsedRailItem
                      key={item.path}
                      $active={pathMatches(currentPath, item.path)}
                      $tone="cyan"
                      aria-label={item.label}
                      title={item.label}
                      onClick={() => handleAssetClassClick(item.path)}
                    >
                      <span>{item.icon}</span>
                    </CollapsedRailItem>
                  ))}
                </CollapsedRailSection>
              ))}
            </CollapsedRail>
          ) : (
            <>
              <SidebarHeading>TRADE ENTRY</SidebarHeading>
              <SidebarNav>
                {menuItems.map(item => {
                  if (item.subItems) {
                    const isActive = item.subItems.some(sub => pathMatches(currentPath, sub.path));
                    const isExpanded = expandedMenus[item.label];
                    return (
                      <div key={item.label} style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <SidebarTab
                          active={false} // Parent tab doesn't get strict active styling
                          onClick={() => toggleMenu(item.label)}
                          style={{ color: isActive ? '#ffffff' : undefined }}
                        >
                          <span>{item.icon}</span>
                          <span style={{ flex: 1 }}>{item.label}</span>
                          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </SidebarTab>

                        {isExpanded && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', paddingLeft: '1.5rem' }}>
                            {item.subItems.map(sub => (
                              <SidebarTab
                                key={sub.path}
                                active={pathMatches(currentPath, sub.path)}
                                onClick={() => handleAssetClassClick(sub.path)}
                                style={{ padding: '0.5rem 1rem', fontSize: '0.8rem' }}
                              >
                                <span>{sub.icon}</span>
                                {sub.label}
                              </SidebarTab>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <SidebarTab
                      key={item.path}
                      active={item.path ? pathMatches(currentPath, item.path) : false}
                      onClick={() => item.path && handleAssetClassClick(item.path)}
                    >
                      <span>{item.icon}</span>
                      {item.label}
                    </SidebarTab>
                  );
                })}
              </SidebarNav>
            </>
          )}
        </Sidebar>

        <ContentArea>
          <Routes>
            {/* Default redirect to fixed-income */}
            <Route index element={<Navigate to="fixed-income" replace />} />

            {/* Fixed Income */}
            <Route path="fixed-income" element={<FixedIncomeTicketScreen />} />

            {/* Equity */}
            <Route path="equity" element={<EquityTicketScreen />} />

            {/* Private Credit */}
            <Route path="leveraged-loan" element={<PrivateFixedIncomeTicketScreen ticketKind="leveraged-loan" />} />
            <Route path="private-fixed-income" element={<PrivateFixedIncomeTicketScreen />} />
            <Route path="posted-credit-event" element={<CreditPostedEventDetailScreen />} />

            {/* CLO Equity — position-anchored BUY-only; lifecycle from position dashboard */}
            <Route path="clo-equity" element={<CLOEquityTicketScreen />} />

            {/* ABF facility — borrowing-base driven; lifecycle from ABF dashboard */}
            <Route path="abf-facility" element={<ABFFacilityTicketScreen />} />
            <Route path="rmbs-tranche" element={<RMBSTrancheTicketScreen />} />
            <Route path="abs-tranche" element={<ABSTrancheTicketScreen />} />
            <Route path="msr" element={<MSRTicketScreen />} />
            <Route path="cmbs-tranche" element={<CMBSTrancheTicketScreen />} />

            {/* Private Equity */}
            <Route path="private-equity" element={<PrivateEquityTicketScreen />} />

            {/* Portco Debt — sponsor-finance origination (BUY-only; lifecycle from position) */}
            <Route path="portco-debt" element={<PortcoDebtTicketScreen />} />

            {/* Real Estate Equity (direct property ownership) */}
            <Route path="real-estate" element={<RealEstateEquityTicketScreen />} />
            {/* Real Estate Debt — commercial mortgage origination */}
            <Route path="real-estate-cml" element={<RealEstateTicketScreen />} />
            <Route path="real-estate-debt" element={<RealEstateTicketScreen />} />
            {/* RML — residential whole-loan pools (originate-once, position-anchored) */}
            <Route path="rml" element={<RMLTicketScreen />} />

            {/* Derivatives — explicit sub-type required (legacy /derivatives was a dead path → fixed-income) */}
            <Route path="derivatives" element={<DerivativesLegacyRedirect />} />
            <Route path="derivatives/ir-swap" element={<DerivativesIRSwapTicketScreen />} />
            <Route path="derivatives/tr-swap" element={<DerivativesTRSwapTicketScreen />} />
            <Route path="derivatives/currency-swap" element={<DerivativesCurrencySwapTicketScreen />} />
            <Route path="derivatives/cd-swap" element={<DerivativesCDSwapTicketScreen />} />
            <Route path="derivatives/swaption" element={<DerivativesSwaptionTicketScreen />} />
            <Route path="derivatives/futures" element={<DerivativesFuturesTicketScreen />} />
            <Route path="derivatives/options" element={<DerivativesOptionsTicketScreen />} />

            {/* FX */}
            <Route path="fx" element={<FXTicketScreen />} />
            <Route path="fx/spot" element={<FXTicketScreen />} />
            <Route path="fx/forward" element={<FXTicketScreen />} />
            <Route path="fx/swap" element={<FXTicketScreen />} />
            <Route path="fx/ndf" element={<FXTicketScreen />} />

            {/* Repo */}
            <Route path="repo" element={<RepoTicketScreen />} />

            {/* Cash */}
            <Route path="cash" element={<CashTransferTicketScreen />} />

            {/* Multi-Leg */}
            <Route path="multi-leg" element={<MultiLegTradeTicket />} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="fixed-income" replace />} />
          </Routes>
        </ContentArea>
      </PageLayout>
    </Container>
  );
};

export default InstitutionalTradeEntry;
